import { useState, useEffect, useCallback } from "react";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";
import { getJobs, toggleJob } from "../utils/api";

const TABS = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

export default function Jobs() {
  const [tab, setTab] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const fetchJobs = useCallback(() => {
    setLoading(true);
    setError("");
    const params = { page };
    if (tab !== "all") params.status = tab;
    if (search) params.search = search;
    getJobs(params)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load jobs"))
      .finally(() => setLoading(false));
  }, [tab, page, search]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleToggle = async (id) => {
    setBusyId(id);
    try {
      await toggleJob(id);
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || "Action failed");
    } finally { setBusyId(null); }
  };

  return (
    <div>
      <h1 className="page-title">Jobs</h1>
      <p className="page-sub">All job postings across the platform.</p>

      <div style={{
        display: "flex", gap: 12, flexWrap: "wrap",
        alignItems: "center", margin: "18px 0 14px"
      }}>
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); setSearch(searchInput.trim()); }}
          style={{ position: "relative", flex: "1 1 260px", maxWidth: 360 }}
        >
          <Search size={15} style={{
            position: "absolute", left: 13, top: "50%",
            transform: "translateY(-50%)", color: "#a0aec0"
          }} />
          <input
            className="input" placeholder="Search by job title…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ paddingLeft: 36 }}
          />
        </form>
        <div className="tabs">
          {TABS.map((t) => (
            <button key={t.id}
              className={`tab ${tab === t.id ? "tab-active" : ""}`}
              onClick={() => { setTab(t.id); setPage(1); }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="error-box" style={{ marginBottom: 14 }}>{error}</div>}

      <div className="card table-wrap">
        {loading ? (
          <div className="empty"><span className="spinner spinner-dark" style={{ width: 26, height: 26 }} /></div>
        ) : !data?.jobs?.length ? (
          <div className="empty">No jobs found{search ? ` for “${search}”` : ""}.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Title</th><th>Organization</th><th>Location</th>
                <th>Specialization</th><th>Status</th><th>Posted</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((j) => (
                <tr key={j._id}>
                  <td style={{ fontWeight: 600 }}>{j.title}</td>
                  <td style={{ color: "#718096" }}>{j.org_name || "—"}</td>
                  <td style={{ color: "#718096" }}>{j.location || "—"}</td>
                  <td style={{ color: "#718096" }}>{j.specialization || "—"}</td>
                  <td>
                    {j.status === "active"
                      ? <span className="badge badge-green">Active</span>
                      : <span className="badge badge-red">Inactive</span>}
                  </td>
                  <td style={{ color: "#718096", whiteSpace: "nowrap" }}>{fmtDate(j.created_at)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      className={`btn btn-sm ${j.status === "active" ? "btn-danger" : "btn-green"}`}
                      disabled={busyId === j._id}
                      onClick={() => handleToggle(j._id)}>
                      {busyId === j._id ? "…" : j.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.pages > 1 && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          marginTop: 14
        }}>
          <span style={{ fontSize: 13, color: "#718096" }}>
            {data.total} job{data.total !== 1 ? "s" : ""} · Page {data.page} of {data.pages}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}>
              <ChevronLeft size={14} /> Prev
            </button>
            <button className="btn btn-ghost btn-sm" disabled={page >= data.pages}
              onClick={() => setPage((p) => p + 1)}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
